/** A grid that either names its tracks or fills to a minimum column width. */

import { createElement, type CSSProperties, type ElementType, type ReactNode } from "react";

import { cn } from "@/lib/cn";

import { lengthOf, mergeStyle, spaceVar, type SpaceStep } from "../internal/style";
import "./Grid.css";

export interface GridProps {
  /** A track count, or a full `grid-template-columns` value. */
  readonly columns?: number | string;
  /** Fill the row instead, at this minimum column width. */
  readonly minColumnWidth?: string | number;
  /** A step on the 4px scale, so `gap={5}` is `--al-space-5`. */
  readonly gap?: SpaceStep;
  readonly align?: CSSProperties["alignItems"];
  readonly as?: ElementType;
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly children?: ReactNode;
}

function tracks(columns: number | string | undefined, minColumnWidth: string | number | undefined) {
  if (typeof columns === "number") return `repeat(${columns}, minmax(0, 1fr))`;
  if (typeof columns === "string") return columns;
  const min = lengthOf(minColumnWidth);
  if (min !== undefined) return `repeat(auto-fill, minmax(min(${min}, 100%), 1fr))`;
  return undefined;
}

export function Grid({
  columns,
  minColumnWidth,
  gap,
  align,
  as = "div",
  className,
  style,
  children,
}: GridProps) {
  return createElement(
    as,
    {
      className: cn("al-grid", className),
      style: mergeStyle(style, {
        gridTemplateColumns: tracks(columns, minColumnWidth),
        gap: spaceVar(gap),
        alignItems: align,
      }),
    },
    children,
  );
}
